"use client";

import type { Tooltip as HeroTooltip } from "@heroui/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { Tooltip } from "../Tooltip";

type TooltipProps = React.ComponentProps<typeof HeroTooltip>;

export interface EllipsisTooltipProps extends Omit<TooltipProps, "children" | "content"> {
  /** The text content to display */
  content: string;
  /** Additional className for the wrapper element */
  className?: string;
  tooltipClassName?: string;
  /** Children to render - if not provided, content will be used */
  children?: React.ReactNode;
  /** The element type to render (default: "div") */
  as?: React.ElementType;
  /** Maximum number of lines before truncating (default: 1) */
  maxLines?: number;
  /**
   * When true, consecutive whitespace in `content`/`children` is collapsed instead of
   * being preserved. Defaults to preserving whitespace.
   */
  collapseWhitespace?: boolean;
}

const lineClampClasses: Record<number, string> = {
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
};

export const EllipsisTooltip = ({
  content,
  className,
  tooltipClassName,
  children,
  as: Component = "div",
  maxLines = 1,
  collapseWhitespace = false,
  ...tooltipProps
}: EllipsisTooltipProps) => {
  const elementRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const isMultiLine = maxLines > 1;
  let whitespaceClass: string | undefined = "whitespace-pre";
  if (isMultiLine) {
    whitespaceClass = "whitespace-pre-wrap";
  }
  if (collapseWhitespace) {
    whitespaceClass = "whitespace-nowrap";
  }
  if (collapseWhitespace && isMultiLine) {
    whitespaceClass = undefined;
  }

  useEffect(() => {
    const checkTruncation = () => {
      if (elementRef.current) {
        const isTrunc = isMultiLine
          ? elementRef.current.scrollHeight > elementRef.current.clientHeight
          : elementRef.current.scrollWidth > elementRef.current.clientWidth;
        setIsTruncated(isTrunc);
      }
    };

    // Use a small delay to ensure layout is complete
    const timeoutId = setTimeout(checkTruncation, 100);

    // Use ResizeObserver to detect element size changes
    const resizeObserver = new ResizeObserver(checkTruncation);
    if (elementRef.current) {
      resizeObserver.observe(elementRef.current);
    }

    window.addEventListener("resize", checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkTruncation);
    };
  }, [isMultiLine, content]);

  const clampClass = isMultiLine ? (lineClampClasses[maxLines] ?? "line-clamp-5") : "overflow-hidden text-ellipsis";

  return (
    <Tooltip
      className={cn(
        "wrap-break-word pointer-events-none line-clamp-5 max-w-[500px]",
        !collapseWhitespace && "whitespace-pre-wrap",
        tooltipClassName,
      )}
      classNames={{ base: "pointer-events-none" }}
      content={content}
      isDisabled={!isTruncated}
      {...tooltipProps}
    >
      <Component
        ref={elementRef}
        className={cn(clampClass, whitespaceClass, isMultiLine && "wrap-break-word", className)}
      >
        {children || content}
      </Component>
    </Tooltip>
  );
};
